import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Eye, FileText, Plus } from 'lucide-react';

/**
 * The case vignette, released in stages (chief complaint → history → exam →
 * labs...). Facilitator sees all stages and reveals them one at a time;
 * students only see revealed stages. Each reveal auto-snapshots the ranking.
 */
export default function CasePanel({ stages, isFacilitator, readOnly, onAddStage, onRevealStage }) {
  const [collapsed, setCollapsed] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');

  const visibleStages = isFacilitator ? stages : stages.filter((s) => s.revealedAt);
  const canManage = isFacilitator && !readOnly;
  if (!visibleStages.length && !canManage) return null;

  const lastRevealedId = [...stages].filter((s) => s.revealedAt)
    .sort((a, b) => a.revealedAt - b.revealedAt).pop()?.id;

  const submitStage = () => {
    if (!title.trim() || !content.trim()) return;
    onAddStage(title, content);
    setTitle('');
    setContent('');
    setShowForm(false);
  };

  return (
    <div className="bg-surface border border-line rounded-card mb-4 sm:mb-6 overflow-hidden">
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="w-full flex items-center justify-between px-4 sm:px-6 py-3 hover:bg-accent-tint transition"
        aria-expanded={!collapsed}
      >
        <span className="flex items-center gap-2 text-body font-medium text-ink">
          <FileText className="w-4 h-4 text-accent" />
          Case
          {visibleStages.length > 0 && (
            <span className="text-meta text-ink-2 font-normal">
              {isFacilitator
                ? `${stages.filter((s) => s.revealedAt).length}/${stages.length} stages revealed`
                : `${visibleStages.length} stage${visibleStages.length === 1 ? '' : 's'}`}
            </span>
          )}
        </span>
        {collapsed ? <ChevronDown className="w-4 h-4 text-ink-2" /> : <ChevronUp className="w-4 h-4 text-ink-2" />}
      </button>

      {!collapsed && (
        <div className="px-4 sm:px-6 pb-4 space-y-3">
          {visibleStages.map((stage, idx) => {
            const hidden = !stage.revealedAt;
            return (
              <div
                key={stage.id}
                className={`item-enter rounded-card border p-3 sm:p-4 ${
                  hidden
                    ? 'border-dashed border-line bg-page opacity-70'
                    : stage.id === lastRevealedId
                      ? 'border-accent bg-accent-tint ring-1 ring-accent/25'
                      : 'border-line'
                }`}
              >
                <div className="flex items-center justify-between gap-2 mb-1">
                  <h3 className="text-body font-semibold text-ink">
                    <span className="text-accent mr-1.5">{idx + 1}.</span>
                    {stage.title}
                    {hidden && <span className="ml-2 text-meta uppercase tracking-wide text-ink-2 font-medium">Hidden from students</span>}
                  </h3>
                  {canManage && hidden && (
                    <button
                      onClick={() => onRevealStage(stage)}
                      className="flex items-center gap-1.5 bg-accent text-white px-3 py-1.5 rounded-ctl text-meta font-medium hover:bg-accent-strong transition flex-shrink-0"
                      title="Reveal to students (snapshots the current ranking first)"
                    >
                      <Eye className="w-3.5 h-3.5" />Reveal
                    </button>
                  )}
                </div>
                <p className="text-body text-ink whitespace-pre-wrap leading-relaxed">{stage.content}</p>
              </div>
            );
          })}

          {canManage && (
            showForm ? (
              <div className="rounded-card border border-accent p-3 sm:p-4 space-y-2 bg-accent-tint">
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Stage title (e.g. 'Initial labs')"
                  className="w-full px-3 py-2 border border-line rounded-ctl text-body focus:ring-2 focus:border-accent focus:ring-accent/25 outline-none"
                  autoFocus
                />
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="Stage content, the information to release at this point in the case…"
                  rows={4}
                  className="w-full px-3 py-2 border border-line rounded-ctl text-body focus:ring-2 focus:border-accent focus:ring-accent/25 outline-none resize-y"
                />
                <div className="flex gap-2 justify-end">
                  <button onClick={() => setShowForm(false)} className="px-3 py-1.5 text-meta text-ink-2 hover:bg-accent-tint rounded-ctl transition">Cancel</button>
                  <button
                    onClick={submitStage}
                    disabled={!title.trim() || !content.trim()}
                    className="px-4 py-1.5 bg-accent text-white text-meta font-medium rounded-ctl hover:bg-accent-strong transition disabled:opacity-50"
                  >
                    Add stage
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setShowForm(true)}
                className="w-full flex items-center justify-center gap-1.5 border border-dashed border-line rounded-card py-2.5 text-meta text-ink-2 hover:border-accent/40 hover:text-accent hover:bg-accent-tint transition"
              >
                <Plus className="w-3.5 h-3.5" />Add case stage
              </button>
            )
          )}
        </div>
      )}
    </div>
  );
}
